import React, { useEffect, useState } from 'react'
import {TrendingUp, DollarSign, Users, Briefcase, ArrowUp, ArrowDown} from 'lucide-react'
import { lumi } from '@/lib/lumi'

export function Analytics() {
  const [stats, setStats] = useState({
    totalAgents: 0,
    totalTasks: 0,
    totalVolume: 0,
    activeAgents: 0,
    completedTasks: 0,
    averageTaskPrice: 0,
    topCategory: '',
    growthRate: 0
  })
  const [loading, setLoading] = useState(true)
  const [transactions, setTransactions] = useState([])
  const completedTasks = stats.completedTasks

  useEffect(() => {
    fetchAnalytics()
  }, [])

  const fetchAnalytics = async () => {
    try {
      const [agentsRes, tasksRes, transactionsRes] = await Promise.all([
        lumi.entities.agents.list({ filter: {}, limit: 1000 }),
        lumi.entities.tasks.list({ filter: {}, limit: 1000 }),
        lumi.entities.transactions.list({ filter: {}, limit: 1000 })
      ])

      // Filter out deleted agents and tasks
      const agents = agentsRes.list.filter(agent => 
        agent.availability !== 'deleted' && 
        !agent.name?.startsWith('[DELETED]')
      )
      const tasks = tasksRes.list.filter(task => 
        task.status !== 'deleted'
      )
      const transactionsList = transactionsRes.list
      setTransactions(transactionsList)

      const activeAgents = agents.filter(a => a.availability === 'online').length
      const completedTasksCount = tasks.filter(t => t.status === 'completed').length
      // Only count verified on-chain transactions with real volume
      const realTransactions = transactionsList.filter(t => t.verifiedOnChain === true)
      const totalVolume = realTransactions.reduce((sum, t) => sum + (t.amount || 0), 0)
      const avgPrice = tasks.length > 0 ? tasks.reduce((sum, t) => sum + (t.budget || 0), 0) / tasks.length : 0

      // Calculate top category
      const categoryCount = {}
      tasks.forEach(t => {
        categoryCount[t.category] = (categoryCount[t.category] || 0) + 1
      })
      const topCategory = Object.keys(categoryCount).sort((a, b) => categoryCount[b] - categoryCount[a])[0] || 'N/A'

      // Calculate real growth rate based on recent agents (last 30 days vs previous 30 days)
      const now = new Date()
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000)
      
      const recentAgents = agents.filter(a => new Date(a.createdAt) > thirtyDaysAgo).length
      const previousAgents = agents.filter(a => {
        const created = new Date(a.createdAt)
        return created > sixtyDaysAgo && created <= thirtyDaysAgo
      }).length
      
      const growthRate = previousAgents > 0 ? ((recentAgents - previousAgents) / previousAgents) * 100 : 0

      setStats({
        totalAgents: agents.length,
        totalTasks: tasks.length,
        totalVolume,
        activeAgents,
        completedTasks: completedTasksCount,
        averageTaskPrice: avgPrice,
        topCategory,
        growthRate
      })
    } catch (error) {
      console.error('Failed to fetch analytics:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-bold text-white mb-2">Platform Analytics</h2>
        <p className="text-gray-400">Real-time marketplace insights</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 border border-purple-500/20 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <Users className="w-10 h-10 text-purple-400" />
            <div className="flex items-center gap-1 text-purple-400 text-sm font-semibold">
              <ArrowUp className="w-4 h-4" />
              {stats.growthRate.toFixed(1)}%
            </div>
          </div>
          <p className="text-gray-400 text-sm mb-1">Total Agents</p>
          <p className="text-3xl font-bold text-white">{stats.totalAgents}</p>
          <p className="text-purple-400 text-xs mt-2">{stats.activeAgents} active now</p>
        </div>

        <div className="bg-gradient-to-br from-purple-600/10 to-purple-700/10 border border-purple-600/20 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <Briefcase className="w-10 h-10 text-purple-300" />
            <div className="flex items-center gap-1 text-purple-300 text-sm font-semibold">
              {completedTasks > 0 && stats.totalTasks > 0 ? (
                <>
                  <ArrowUp className="w-4 h-4" />
                  {((completedTasks / stats.totalTasks) * 100).toFixed(1)}%
                </>
              ) : (
                <span className="text-gray-500">N/A</span>
              )}
            </div>
          </div>
          <p className="text-gray-400 text-sm mb-1">Total Tasks</p>
          <p className="text-3xl font-bold text-white">{stats.totalTasks}</p>
          <p className="text-purple-300 text-xs mt-2">{stats.completedTasks} completed</p>
        </div>

        <div className="bg-gradient-to-br from-purple-400/10 to-purple-500/10 border border-purple-400/20 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <DollarSign className="w-10 h-10 text-purple-300" />
            <div className="flex items-center gap-1 text-purple-300 text-sm font-semibold">
              {transactions.length > 0 ? (
                <>
                  <ArrowUp className="w-4 h-4" />
                  {((transactions.length / Math.max(stats.totalTasks, 1)) * 100).toFixed(1)}%
                </>
              ) : (
                <span className="text-gray-500">N/A</span>
              )}
            </div>
          </div>
          <p className="text-gray-400 text-sm mb-1">Total Volume</p>
          <p className="text-3xl font-bold text-white">${stats.totalVolume.toFixed(0)}</p>
          <p className="text-gray-400 text-xs mt-2">All-time</p>
        </div>

        <div className="bg-gradient-to-br from-purple-700/10 to-purple-800/10 border border-purple-700/20 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <TrendingUp className="w-10 h-10 text-purple-400" />
            <div className="flex items-center gap-1 text-purple-300 text-sm font-semibold">
              {stats.totalTasks > 0 ? (
                <>
                  <ArrowUp className="w-4 h-4" />
                  {((stats.completedTasks / stats.totalTasks) * 100).toFixed(1)}%
                </>
              ) : (
                <span className="text-gray-500">N/A</span>
              )}
            </div>
          </div>
          <p className="text-gray-400 text-sm mb-1">Avg Task Price</p>
          <p className="text-3xl font-bold text-white">${stats.averageTaskPrice.toFixed(0)}</p>
          <p className="text-gray-400 text-xs mt-2">Median value</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-black/50 border border-gray-800 rounded-xl p-6">
          <h3 className="text-xl font-bold text-white mb-4">Top Category</h3>
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Most Active</span>
            <span className="text-2xl font-bold text-purple-400">{stats.topCategory}</span>
          </div>
          <div className="mt-4 h-2 bg-gray-900 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-purple-500 to-purple-700" style={{ width: '75%' }}></div>
          </div>
        </div>

        <div className="bg-black/50 border border-gray-800 rounded-xl p-6">
          <h3 className="text-xl font-bold text-white mb-4">Success Rate</h3>
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Completion Rate</span>
            <span className="text-2xl font-bold text-purple-400">
              {stats.totalTasks > 0 ? ((stats.completedTasks / stats.totalTasks) * 100).toFixed(1) : 0}%
            </span>
          </div>
          <div className="mt-4 h-2 bg-gray-900 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-purple-500 to-purple-700" 
              style={{ width: `${stats.totalTasks > 0 ? (stats.completedTasks / stats.totalTasks) * 100 : 0}%` }}
            ></div>
          </div>
        </div>
      </div>

      <div className="bg-black/50 border border-gray-800 rounded-xl p-6">
        <h3 className="text-xl font-bold text-white mb-6">Real-Time Activity</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Active Agents</span>
            <span className="text-2xl font-bold text-purple-400">{stats.activeAgents}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Pending Tasks</span>
            <span className="text-2xl font-bold text-purple-400">{stats.totalTasks - stats.completedTasks}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Total Transactions</span>
            <span className="text-2xl font-bold text-purple-400">{transactions.length}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Platform Growth</span>
            <span className="text-2xl font-bold text-purple-400">{stats.growthRate >= 0 ? '+' : ''}{stats.growthRate.toFixed(1)}%</span>
          </div>
        </div>
      </div>
    </div>
  )
}